import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button } from '../components/common';
import { post, API_URL } from '../utils/api';

// Validation schema
const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

function ResetPassword() {
  const [error, setError] = useState(null);
  const [isTokenValid, setIsTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    // Check if token is present and valid on component mount
    if (!token) {
      setIsTokenValid(false);
      return;
    }

    // Validate token with server
    const validateToken = async () => {
      try {
        await post(`${API_URL}/auth/validate-reset-token`, { token });
        setIsTokenValid(true);
      } catch (err) {
        // console.error('Token validation error:', err);
        setIsTokenValid(false);
        
        if (err.response?.status === 400) {
          setError('This password reset link has expired or is invalid.');
        } else {
          setError('Unable to validate reset token. Please try again.');
        }
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    
    try {
      // console.log('Resetting password with token');
      
      await post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword: values.password
      });
      
      setIsSuccess(true);
    } catch (err) {
      // console.error('Password reset error:', err);
      
      if (err.response?.status === 400) {
        setError('This password reset link has expired or is invalid. Please request a new one.');
      } else if (err.response?.status === 422) {
        setError(err.response.data?.message || 'Password does not meet requirements.');
      } else {
        setError('An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking token
  if (isTokenValid === null) {
    return (
      <div className="max-w-md mx-auto mt-8 mb-12">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-gray-300 border-t-hooray-red rounded-full mb-4"></div>
          <p className="text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (isTokenValid === false) {
    return (
      <div className="max-w-md mx-auto mt-8 mb-12">
        <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">
          Invalid Reset Link
        </h1>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            {/* Error icon */}
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <p className="text-gray-700 mb-4">
              {error || 'This password reset link is invalid or has expired.'}
            </p>
            
            <p className="text-sm text-gray-600 mb-6">
              Password reset links expire after 24 hours for security purposes.
            </p>
          </div>
          
          <div className="space-y-4">
            <Link to="/forgot-password">
              <Button variant="cta" className="w-full mb-3">
                Request New Reset Link
              </Button>
            </Link>
            
            <div className="text-center">
              <Link 
                to="/login" 
                className="text-sm text-hooray-red hover:text-hooray-yellow font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto mt-8 mb-12">
        <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">
          Password Reset Successful
        </h1>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            {/* Success icon */}
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <p className="text-gray-700 mb-4">
              Your password has been successfully reset!
            </p>
            
            <p className="text-sm text-gray-600 mb-6">
              You can now log in with your new password.
            </p>
          </div>
          
          <Button 
            onClick={() => navigate('/login')}
            variant="cta" 
            className="w-full"
          >
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div className="max-w-md mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">
        Create New Password
      </h1>
      
      <p className="text-center text-gray-600 mb-6">
        Please enter your new password below.
      </p>
      
      {/* Error message display */}
      {error && (
        <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md" 
             role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          validationSchema={ResetPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors, values }) => (
            <Form>
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <Field
                  type="password"
                  name="password"
                  id="password"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <Field
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              

              
              <Button
                type="submit"
                variant="cta"
                disabled={isSubmitting}
                className="w-full mb-4"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-hooray-red hover:text-hooray-yellow font-medium"
                >
                  Back to Login
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ResetPassword; 